/* HC Planner calculation engine — weeks are Monday-start, horizon = 26 weeks from the current Monday. */
export const AMBER = "#F59E0B", VIOLET = "#8B5CF6", TEAL = "#14B8A6", GREEN = "#10B981", RED = "#EF4444";
const DAY = 86400000;
export const iso = (d) => d.toISOString().slice(0, 10);
export const parse = (s) => new Date(s + "T00:00:00Z");
export const addDays = (s, n) => iso(new Date(parse(s).getTime() + n * DAY));
export const monday = (s) => { const d = parse(s); const w = (d.getUTCDay() + 6) % 7; return addDays(s, -w); };
export const ym = (s) => s.slice(0, 7);
export const mLabel = (m) => parse(m + "-01").toLocaleString("en", { month: "short", year: "numeric", timeZone: "UTC" });
export const dLabel = (s) => parse(s).toLocaleString("en", { day: "numeric", month: "short", timeZone: "UTC" });
export const ceil = Math.ceil, round = Math.round, floor = Math.floor;
const num = (v) => (v === "" || v == null || isNaN(Number(v)) ? null : Number(v));
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export const WEEKS_PER_MONTH = 4.33;

export function horizon(today, n = 26) {
  const start = monday(today);
  return Array.from({ length: n }, (_, i) => addDays(start, 7 * i));
}
export const monthsOf = (weeks) => [...new Set(weeks.map(ym))];
// week number inside its month (Wk1..Wk5 by Mondays)
export const wkNo = (s) => Math.floor((Number(s.slice(8, 10)) - 1) / 7) + 1;

// ---------- inputs resolved per department ----------
export function resolveA(plan, dept) {
  const own = Object.fromEntries(Object.entries(plan.partA?.[dept] || {}).filter(([, v]) => v !== "" && v != null && !isNaN(Number(v))));
  return { ...plan.defaults, ...own };
}
const actualsFor = (plan, m, dept) => plan.actuals?.[m]?.[dept] || null;
const enteredMonths = (plan, dept) => Object.keys(plan.actuals || {}).filter((m) => plan.actuals[m]?.[dept]).sort();
const monthAvgShrink = (a) => { const v = (a?.shrink || []).map(num).filter((x) => x != null); return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null; };

// ---------- hard headcount cap ----------
export function capOf(A) {
  const ceiling = num(A.maxHC) > 0 ? num(A.maxHC) : null;
  const buffer = clamp(round(num(A.capBuffer) ?? 4), 0, 4);
  return { ceiling, buffer, hardCap: ceiling != null ? ceiling + buffer : null, countTrainees: num(A.countTrainees) !== 0 };
}
export function validateA(A) {
  const errors = {}, warnings = {};
  const ceiling = num(A.maxHC), buf = num(A.capBuffer), approved = num(A.approved), opening = num(A.openingHC);
  if (ceiling != null && ceiling < 0) errors.maxHC = "Maximum HC cannot be negative";
  if (buf != null && (buf < 0 || buf > 4 || buf !== round(buf))) errors.capBuffer = "Buffer must be 0–4";
  if (ceiling > 0 && approved != null && approved > ceiling) errors.approved = `Max allowed is ${ceiling}`;
  if (ceiling > 0 && opening != null && opening > ceiling + clamp(buf ?? 4, 0, 4)) warnings.openingHC = `Above hard cap (${ceiling + clamp(buf ?? 4, 0, 4)}) by ${opening - ceiling - clamp(buf ?? 4, 0, 4)}`;
  return { errors, warnings, ok: !Object.keys(errors).length };
}

export function passRateUsed(plan, dept, mode) {
  const A = resolveA(plan, dept);
  if (mode === "assumed") return { rate: A.passRate / 100, tag: "baseline", n: 0 };
  const done = (plan.batches || []).filter((b) => b.dept === dept && num(b.passed) != null && num(b.joined) > 0);
  if (!done.length) return { rate: A.passRate / 100, tag: "baseline", n: 0 };
  const j = done.reduce((s, b) => s + num(b.joined), 0), p = done.reduce((s, b) => s + num(b.passed), 0);
  return { rate: j ? p / j : A.passRate / 100, tag: `${done.length} batch avg`, n: done.length };
}

export function attritionUsed(plan, dept, mode, month) {
  const A = resolveA(plan, dept);
  if (mode === "actual" && plan.adj?.attrition !== "" && plan.adj?.attrition != null) return { rate: Number(plan.adj.attrition) / 100, tag: "scenario" };
  if (mode === "actual") {
    const ms = enteredMonths(plan, dept).filter((m) => m <= month);
    for (let i = ms.length - 1; i >= 0; i--) {
      const a = actualsFor(plan, ms[i], dept);
      if (num(a.attrition) != null && num(a.startHC) > 0) return { rate: num(a.attrition) / num(a.startHC), tag: "1-mo", month: ms[i] };
    }
  }
  return { rate: A.attrition / 100, tag: "base" };
}

function shrinkFor(plan, dept, mode, week) {
  const A = resolveA(plan, dept);
  if (mode === "assumed") return { pct: A.shrinkage, actual: false };
  if (plan.adj?.shrinkage !== "" && plan.adj?.shrinkage != null) return { pct: Number(plan.adj.shrinkage), actual: false };
  const m = ym(week), a = actualsFor(plan, m, dept);
  const v = a ? num(a.shrink?.[wkNo(week) - 1]) : null;
  if (v != null) return { pct: v, actual: true };
  const avg = monthAvgShrink(a);
  if (avg != null) return { pct: avg, actual: false };
  const prev = enteredMonths(plan, dept).filter((x) => x < m).reverse().map((x) => monthAvgShrink(actualsFor(plan, x, dept))).find((x) => x != null);
  return { pct: prev ?? A.shrinkage, actual: false };
}

function approvedFor(plan, dept, mode, m) {
  const A = resolveA(plan, dept);
  if (mode === "assumed") return A.approved;
  const ms = enteredMonths(plan, dept).filter((x) => x <= m).reverse();
  for (const x of ms) { const v = num(actualsFor(plan, x, dept)?.approved); if (v != null) return v; }
  return A.approved;
}

function openingHC(plan, dept, mode, firstMonth) {
  const A = resolveA(plan, dept);
  if (mode === "assumed") return { hc: A.openingHC, src: "base" };
  const cur = actualsFor(plan, firstMonth, dept);
  if (num(cur?.startHC) != null) return { hc: num(cur.startHC), src: "act" };
  const ms = enteredMonths(plan, dept).filter((x) => x < firstMonth).reverse();
  for (const x of ms) { const a = actualsFor(plan, x, dept); if (num(a.endHC) != null) return { hc: num(a.endHC), src: "act" }; if (num(a.startHC) != null) return { hc: Math.max(0, num(a.startHC) - (num(a.attrition) || 0)), src: "act" }; }
  return { hc: A.openingHC, src: "base" };
}

// ---------- batches ----------
export const inTraining = (b) => Math.max(0, (num(b.joined) || 0) - (num(b.absconded) || 0) - (num(b.failed) || 0) - (num(b.passed) || 0));
export const batchStatus = (b, today) => (num(b.passed) != null ? "Completed" : b.trainStart > today ? "Upcoming" : "In training");
// partly passed hires round down (8 × 85% = 6.8 → 6 on the floor)
export const expectedLive = (b, rate) => (num(b.passed) != null ? num(b.passed) : Math.min(inTraining(b), floor((num(b.joined) || 0) * rate)));

export const CAP_STATUS = { schedule: ["Schedule", GREEN], reduced: ["Reduced", AMBER], delayed: ["Delayed", AMBER], blocked: ["No hiring", RED] };
export const capChipText = (p) => {
  if (!p) return "";
  if (p.status === "blocked") return p.text;
  const parts = [];
  if (p.delayWeeks) parts.push(`Delayed ${p.delayWeeks} wk${p.delayWeeks > 1 ? "s" : ""}`);
  if (p.hire < p.hiresRequested) parts.push(`Reduced to ${p.hire}`);
  return parts.length ? parts.join(" · ") : "Schedule";
};
// remark shown on the training-start week when the cap changed the batch
export const capRemark = (p) => { const parts = []; if (p.hire < p.hiresRequested) parts.push(`Reduced to ${p.hire} (cap)`); if (p.delayWeeks) parts.push(`Delayed ${p.delayWeeks} week${p.delayWeeks > 1 ? "s" : ""} (cap)`); return parts.join(" · "); };

// ---------- remembered department (shared by Dashboard, Week-wise plan, Month update) ----------
const DEPT_KEY = "hc_dept";
export function loadDept(depts) {
  let v = null;
  try { v = localStorage.getItem(DEPT_KEY); } catch { v = null; }
  if (v && depts.some((d) => d.id === v)) return v;
  return (depts.find((d) => String(d.name).trim().toLowerCase() === "111 inbound") || depts[0])?.id;
}
export function saveDept(id) { try { localStorage.setItem(DEPT_KEY, id); } catch { /* storage blocked */ } }

// ---------- the simulation ----------
export function simulate(plan, dept, mode) {
  const today = plan.today, weeks = horizon(today), A = resolveA(plan, dept), months = monthsOf(weeks), cap = capOf(A);
  const pr = passRateUsed(plan, dept, mode), rate = pr.rate;
  const leadWeeks = A.sourceWeeks + A.trainWeeks;
  const hired = (plan.batches || []).filter((b) => b.dept === dept);
  const first = openingHC(plan, dept, mode, months[0]);
  const rows = [];
  const liveAt = {}; hired.forEach((b) => { const w = monday(b.goLive); liveAt[w] = (liveAt[w] || []).concat([{ ...b, live: expectedLive(b, rate), hired: true }]); });
  const proposals = [];
  const build = () => {
    rows.length = 0;
    let hcX = first.hc, lastLive = null;
    weeks.forEach((w, i) => {
      const m = ym(w), wk = wkNo(w), a = mode === "actual" ? actualsFor(plan, m, dept) : null;
      const approved = approvedFor(plan, dept, mode, m);
      const sh = shrinkFor(plan, dept, mode, w);
      let startSrc = "fc";
      if (i > 0 && wk === 1 && a && num(a.startHC) != null) { hcX = num(a.startHC); startSrc = "act"; }
      const startX = hcX, active = round(hcX);
      const at = attritionUsed(plan, dept, mode, m);
      const entered = !!(a && num(a.attrition) != null);
      let lossX = 0, attrSrc = "fc";
      if (entered) { if (wk === 1) { lossX = num(a.attrition); attrSrc = "act"; } }
      else lossX = (hcX * at.rate) / WEEKS_PER_MONTH;
      const lives = (liveAt[w] || []).concat(proposals.filter((p) => p.goLive === w).map((p) => ({ ...p, hired: false })));
      const addition = lives.reduce((s, b) => s + b.live, 0);
      const afterLoss = Math.max(0, hcX - lossX);
      const attrition = active - round(afterLoss);
      hcX = afterLoss + addition;
      const hc = round(hcX);
      const trainingNow = hired.filter((b) => monday(b.trainStart) <= w && monday(b.goLive) > w).map((b) => ({ name: b.name, n: inTraining(b) })).concat(proposals.filter((p) => p.trainStart <= w && p.goLive > w).map((p) => ({ name: `proposed ${p.name}`, n: p.hire })));
      const trainees = trainingNow.reduce((s, b) => s + b.n, 0);
      const totalCapX = Math.max(hcX, startX) + (cap.countTrainees ? trainees : 0), totalCap = round(totalCapX);
      const headroom = cap.hardCap != null ? cap.hardCap - totalCap : null;
      const remarks = [];
      lives.forEach((b) => remarks.push({ kind: b.hired ? "live" : "proposed", text: `${b.hired ? "Batch" : "Proposed batch"} ${b.name} live +${b.live}` }));
      if (attrition) remarks.push({ kind: "attr", text: `Attrition −${attrition} (${attrSrc === "act" ? "entered" : `${(at.rate * 100).toFixed(1)}%/mo ${at.tag}, spread weekly`})` });
      hired.concat(proposals).forEach((b) => { if (monday(b.trainStart) === w) { remarks.push({ kind: "train", text: `Training starts ${b.name} (${b.hire || b.joined})` }); if (!b.hired && b.hiresRequested != null && capRemark(b)) remarks.push({ kind: "cap", text: capRemark(b) }); } if (monday(addDays(b.trainStart, -7 * A.sourceWeeks)) === w) remarks.push({ kind: "source", text: `Start sourcing ${b.name} · hire ${b.hire || b.joined}` }); });
      const overCap = cap.hardCap != null && totalCap > cap.hardCap;
      if (lives.length) lastLive = { names: lives.map((b) => b.name), w };
      const causedBy = overCap ? [...new Set(lives.map((b) => `${b.name} live +${b.live}`).concat(trainingNow.filter((b) => b.n > 0).map((b) => `${b.name} (${b.n} in training)`)))] : [];
      if (overCap && !causedBy.length && lastLive) causedBy.push(`${lastLive.names.join(", ")} live since ${dLabel(lastLive.w)}`);
      if (overCap) remarks.push({ kind: "overcap", text: `Over hard cap ${cap.hardCap} by ${totalCap - cap.hardCap} — error${causedBy.length ? ` · caused by ${causedBy.join(", ")}` : " · productive HC alone is above the cap"}` });
      const gap = hc - approved;
      rows.push({ w, m, wk, approved, shrink: sh.pct, shrinkActual: sh.actual, ceiling: cap.ceiling, hardCap: cap.hardCap, active, attrition, attrSrc, attrRate: at.rate, attrTag: at.tag, addition, batches: lives, total: hc, totalX: hcX, startX, trainees, totalCap, totalCapX, headroom, overCap, causedBy, gap, remarks, startSrc, isActual: !!a, isCurrent: i === 0 });
    });
  };
  build();
  // ---- batch sizing with the cap: candidates in date order, each checked at its training-start week and at go-live ----
  const N = weeks.length;
  let letter = 65 + Math.min(hired.length, 20), s = 0, blocked = null, guard = 0;
  while (guard++ < 60 && proposals.length < 12) {
    let t = s + A.sourceWeeks, g = t + A.trainWeeks;
    if (g >= N) break;
    if (rows[g].approved - round(rows[g].totalX) <= 0) { s++; continue; }
    const s0 = s, steps = [];
    let placed = false;
    while (g < N) {
      // floor at go-live = productive HC after attrition and any other batch going live that week (before this batch)
      const floorG = round(rows[g].totalX), need = rows[g].approved - floorG, delay = s - s0;
      if (need <= 0) break;
      const requested = clamp(ceil(need / rate), A.minBatch, A.maxBatch);
      let hires = requested;
      // hiring never aims above Maximum HC, and the go-live week itself must stay within the hard cap
      const liveRoom = cap.hardCap == null ? Infinity : cap.hardCap - rows[g].totalCap;
      while (hires > 0 && ((cap.ceiling != null && floorG + floor(hires * rate) > cap.ceiling) || floor(hires * rate) > liveRoom)) hires--;
      // headroom while in training = hard cap − (productive HC after go-lives that week + trainees already in training + batches already planned)
      const peakBefore = cap.countTrainees && g > t ? Math.max(...rows.slice(t, g).map((r) => r.totalCap)) : null;
      const headroom = cap.hardCap == null || peakBefore == null ? Infinity : cap.hardCap - peakBefore;
      const line = { s: weeks[s], t: weeks[t], g: weeks[g], floorG, need, requested, afterCeiling: hires, peakBefore, headroom: headroom === Infinity ? null : headroom, delay };
      if (hires < A.minBatch) { line.result = `${cap.ceiling != null && floorG + floor(A.minBatch * rate) > cap.ceiling ? `productive HC would pass the ceiling ${cap.ceiling}` : `no room under the hard cap ${cap.hardCap} at go-live`} — wait`; steps.push(line); s++; t++; g++; continue; }
      let status;
      if (hires <= headroom) status = delay ? "delayed" : "schedule";
      else if (headroom >= A.minBatch) { hires = headroom; status = "reduced"; }
      else { line.result = `headroom ${headroom} < minimum batch ${A.minBatch} — move 1 week`; steps.push(line); s++; t++; g++; continue; }
      line.result = `${status === "schedule" ? "Schedule" : status === "reduced" ? `Reduced to ${hires} (cap)` : `Delayed ${delay} wk (cap)`} · hire ${hires}`;
      steps.push(line);
      const leavers = rows.slice(0, g + 1).reduce((x, r) => x + r.attrition, 0), live = floor(hires * rate), covered = Math.min(ceil(need), live);
      proposals.push({ id: `P${letter}`, name: String.fromCharCode(letter++), dept, hire: hires, hiresRequested: requested, live, goLive: weeks[g], trainStart: weeks[t], source: weeks[s], status, delayWeeks: delay, headroom: headroom === Infinity ? null : headroom, peakTotal: (peakBefore ?? rows[g].totalCap) + (cap.countTrainees && peakBefore != null ? hires : live), hardCap: cap.hardCap, ceiling: cap.ceiling, floorAtGoLive: floorG, shortfall: ceil(need), leavers: Math.min(leavers, covered), gapPart: Math.max(0, covered - leavers), steps });
      build();
      placed = true;
      break;
    }
    if (!placed) { if (!steps.length) { s++; continue; } if (!proposals.length) blocked = { status: "blocked", text: steps.some((x) => x.result.includes("ceiling")) ? "No hiring – over cap" : "No hiring – cap reached", steps, since: weeks[s0] }; break; }
    s++;
  }
  const reach = addDays(weeks[0], 7 * leadWeeks);
  const coverWeeks = rows.filter((r) => r.gap < 0 && r.w < reach).map((r) => ({ w: r.w, n: -r.gap }));
  const lowest = rows.reduce((a, r) => (r.gap < a.gap ? r : a), rows[0]);
  const leavers = rows.reduce((s, r) => s + r.attrition, 0);
  const peak = rows.reduce((a, r) => (r.totalCapX > a.totalCapX ? r : a), rows[0]);
  const overApproved = !proposals.length && !blocked && rows.every((r) => r.gap >= 0) && rows[0].total > rows[0].approved;
  const capErrors = rows.filter((r) => r.overCap).map((r) => r.w);
  const capErrorDetails = rows.filter((r) => r.overCap).map((r) => ({ w: r.w, over: r.totalCap - r.hardCap, total: r.totalCap, causedBy: r.causedBy }));
  return { dept, mode, A, cap, weeks, months, rows, proposals, blocked: blocked || (overApproved ? { status: "blocked", text: "No hiring – over approved", steps: [] } : null), coverWeeks, lowest, leavers, hiresToReplace: ceil(leavers / rate), passRate: pr, opening: first, attr: attritionUsed(plan, dept, mode, months[0]), hiredBatches: hired, leadWeeks, peak: { total: peak.totalCap, w: peak.w, floor: peak.totalCap - (cap.countTrainees ? peak.trainees : 0), trainees: peak.trainees }, capErrors, capErrorDetails };
}

export function monthTable(sim, plan) {
  return sim.months.map((m) => {
    const rs = sim.rows.filter((r) => r.m === m), last = rs[rs.length - 1], a = plan.actuals?.[m]?.[sim.dept];
    const bs = (plan.batches || []).filter((b) => b.dept === sim.dept);
    const joined = bs.filter((b) => ym(b.trainStart) === m).reduce((s, b) => s + (num(b.joined) || 0), 0);
    const passed = bs.filter((b) => ym(b.goLive) === m && num(b.passed) != null).reduce((s, b) => s + num(b.passed), 0);
    const lost = bs.filter((b) => ym(b.goLive) === m).reduce((s, b) => s + (num(b.absconded) || 0) + (num(b.failed) || 0), 0);
    const training = last.trainees;
    const attr = rs.reduce((s, r) => s + r.attrition, 0);
    const sourcing = sim.proposals.filter((p) => ym(p.source) === m).reduce((s, p) => s + p.hire, 0);
    return { m, approved: last.approved, startHC: rs[0].active, shrink: rs.reduce((s, r) => s + r.shrink, 0) / rs.length, attr, attrPct: rs[0].active ? (100 * attr) / rs[0].active : 0, joined, passed, lost, training, live: rs.reduce((s, r) => s + r.addition, 0), endHC: last.total, peakTotal: Math.max(...rs.map((r) => r.totalCap)), hardCap: last.hardCap, gap: last.gap, sourcing, isActual: !!a, src: a ? "act" : "fc", weeks: rs };
  });
}

export function nextBatch(sim) {
  const p = sim.proposals[0];
  const today = sim.weeks[0];
  const training = sim.hiredBatches.filter((b) => b.trainStart <= today && monday(b.goLive) > today).reduce((s, b) => s + inTraining(b), 0);
  if (!p) {
    if (sim.blocked) return { urgency: "blocked", label: sim.blocked.text, p: null, blocked: sim.blocked, training };
    return { urgency: sim.coverWeeks.length ? "cover" : "none", label: sim.coverWeeks.length ? "Cover now" : "No batch", p: null, training };
  }
  const wks = Math.round((parse(p.source) - parse(today)) / (7 * DAY));
  const urgency = wks <= 0 ? "now" : wks <= 3 ? "soon" : "planned";
  return { urgency, label: { now: "Act now", soon: "Soon", planned: "Planned" }[urgency], p, next: sim.proposals[1] || null, training };
}

export const gapTone = (g) => (g < 0 ? "bad" : g > 0 ? "warn" : "good");
export const gapText = (g) => (g < 0 ? `${-g} short` : g > 0 ? `+${g} over` : "On target");
